import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CreatePeriodFormValues,
  CreateReportingPeriodRequest,
} from "./types/types";
import { createReportingPeriod } from "@/api/ReportingPeriods";
import { useAtomValue, useSetAtom } from "jotai";
import { FundAtom } from "@/store/FundStateAtom";
import {
  ReportingPeriodAtom,
  SelectedReportingPeriodAtom,
} from "@/store/ReportingPeriodStateAtom";
import { ReportingPeriod } from "@/types/ReportingPeriod";

interface UseCreateReportingPeriodController {
  open: boolean;
  setOpen: (open: boolean) => void;
  formValues: CreatePeriodFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<CreatePeriodFormValues>>;
  onSubmit: () => void;
  trigger?: React.ReactNode;
  isCreating: boolean;
  createPeriod: (
    data: CreateReportingPeriodRequest
  ) => Promise<ReportingPeriod | null>;
  openReportingPeriods: ReportingPeriod[];
}

export function useCreateReportingPeriodController({
  onSubmit,
  trigger,
}: {
  onSubmit: (values: CreatePeriodFormValues) => void;
  trigger?: React.ReactNode;
}): UseCreateReportingPeriodController {
  const selectedFund = useAtomValue(FundAtom);
  const setSelectedReportingPeriod = useSetAtom(SelectedReportingPeriodAtom);
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = React.useState(false);
  const reportingPeriods = useAtomValue(ReportingPeriodAtom);
  const [formValues, setFormValues] = React.useState<CreatePeriodFormValues>({
    isOpen: true,
    period: new Date(),
  });

  const openReportingPeriods = React.useMemo(() => {
    return reportingPeriods.filter((period) => period.isOpen);
  }, [reportingPeriods]);

  const createPeriod = async (
    data: CreateReportingPeriodRequest
  ): Promise<ReportingPeriod | null> => {
    try {
      setIsCreating(true);
      const result = await createReportingPeriod(selectedFund[0].fundId, data);

      // Invalidate and refetch reporting periods
      queryClient.invalidateQueries({ queryKey: ["reportingPeriods"] });

      // Set the newly created period as the selected period if returned
      if (result !== null) {
        setSelectedReportingPeriod(result);
      }

      return result;
    } catch (error) {
      console.error("Error creating reporting period:", error);
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmit = () => {
    onSubmit(formValues);
    setFormValues({
      isOpen: true,
      period: new Date(),
    });
    setOpen(false);
  };

  return {
    open,
    setOpen,
    formValues,
    setFormValues,
    onSubmit: handleSubmit,
    trigger,
    isCreating,
    createPeriod,
    openReportingPeriods,
  };
}
